import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './client.css';
import '../tasks/task.css';
import '../shared/companyPicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser, selectUserId, selectUsers, setIsLogOut, setUsers } from '../login/loginSlice';
import ClientMenuModal from './ClientMenuModal';
import AddWorkspaceCompanyModal from './AddWorkspaceCompanyModal';
import { useNavigate } from 'react-router-dom';
import { arrowLIcon, addAccountIcon, outIcon, deleteIcon } from '../../static/svgs';
import { createTask } from '../tasks/taskApi';
import DeleteModal from './DeleteModal';
import {
    getBusinessCompanies,
    getEmployeeAccessibleCompanies,
    getUserData,
    setBusinessCompanyInWork,
    setEmployeeCompanyInWork,
} from '../login/loginApi';
import { url } from '../../static/env';
import CompanyPickerList from '../shared/CompanyPickerList';
import CompanyPaymentBlock from '../shared/CompanyPaymentBlock';
import { applyAccountSwitch, resolveBusinessAccountId } from '../../utils/accountUtils';
import { getPacks } from '../documents/packApi';
import { buildBuhBillPaymentSummary } from '../../utils/buhBillPayment';
import { useSectionMenuReset } from '../../context/SectionMenuResetContext';

function mapOwnedToPickerItem(company, businessUserId) {
    const legacyId = company.legacy_user_id != null ? Number(company.legacy_user_id) : null;
    return {
        user_id: legacyId || Number(company.company_id),
        company_name: company.name,
        company_id: company.company_id,
        legacy_user_id: legacyId,
        inn: company.inn,
        address: company.address,
        is_company_record: true,
        is_company_context: true,
        access_mode: 'owner',
        business_user_id: businessUserId != null ? Number(businessUserId) : null,
        has_legacy_user: !!legacyId,
        in_work: !!company.in_work,
        on_service: !!company.on_service,
        employee_user_id: null,
    };
}

function mapDelegatedToPickerItem(company, employeeUserId) {
    const legacyId = company.legacy_user_id != null ? Number(company.legacy_user_id) : null;
    const companyId = Number(company.company_id);
    const guestId = employeeUserId != null ? Number(employeeUserId) : null;
    return {
        user_id: legacyId || companyId,
        company_name: company.name,
        company_id: companyId,
        legacy_user_id: legacyId,
        inn: company.inn || null,
        address: company.address || null,
        is_company_record: true,
        is_company_context: true,
        access_mode: 'delegated',
        business_user_id: guestId,
        company_owner_user_id: company.business_user_id != null ? Number(company.business_user_id) : null,
        accountant_id: company.accountant_id || null,
        has_legacy_user: !!legacyId,
        in_work: !!company.in_work,
        on_service: !!company.on_service,
        employee_user_id: guestId,
        access_documents: !!company.access_documents,
        access_invoices: !!company.access_invoices,
        access_bank: !!company.access_bank,
        access_tax: !!company.access_tax,
        access_salary: !!company.access_salary,
        access_buh_bill: !!company.access_buh_bill,
        owner_photo_path: company.owner_photo_path || null,
        owner_name: company.owner_name || null,
    };
}

const Client = ({ setIsLoggedIn }) => {
    const [isModalDeleteAccount, setIsModalDeleteAccount] = useState(false);
    const [companyMenuUser, setCompanyMenuUser] = useState(null);
    const [isSingleDetailMenuOpen, setIsSingleDetailMenuOpen] = useState(false);
    const [deleteTargetUser, setDeleteTargetUser] = useState(null);
    const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
    const [client, setClient] = useState({});
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [paymentByUserId, setPaymentByUserId] = useState({});
    const [removingFromWork, setRemovingFromWork] = useState(false);
    const [showCompanyDetail, setShowCompanyDetail] = useState(false);

    const users = useSelector(selectUsers);
    const current_user = useSelector(selectCurrentUser);
    const user_id = useSelector(selectUserId);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const businessAccountId = useMemo(
        () => resolveBusinessAccountId({ users, currentUser: current_user, userId: user_id }),
        [users, current_user, user_id]
    );

    const companyPickerUsers = companies;

    const loadCompanies = useCallback(async () => {
        if (!businessAccountId) {
            setCompanies([]);
            return;
        }
        setCompaniesLoading(true);
        try {
            const [ownedResult, delegatedResult] = await Promise.all([
                getBusinessCompanies(businessAccountId),
                getEmployeeAccessibleCompanies(businessAccountId, 'all'),
            ]);
            const byId = new Map();
            if (ownedResult.success) {
                (ownedResult.companies || []).forEach((c) => {
                    if (!c.company_id) return;
                    byId.set(Number(c.company_id), mapOwnedToPickerItem(c, businessAccountId));
                });
            }
            if (delegatedResult.success) {
                (delegatedResult.companies || []).forEach((c) => {
                    if (!c.company_id) return;
                    const id = Number(c.company_id);
                    if (byId.has(id)) return;
                    byId.set(id, mapDelegatedToPickerItem(c, businessAccountId));
                });
            }
            const merged = [...byId.values()].sort((a, b) =>
                String(a.company_name || '').localeCompare(String(b.company_name || ''), 'ru')
            );
            setCompanies(merged);
        } catch (e) {
            console.error(e);
            setCompanies([]);
        } finally {
            setCompaniesLoading(false);
        }
    }, [businessAccountId]);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    useEffect(() => {
        if (companyPickerUsers.length === 0) {
            setSelectedCompany(null);
            setShowCompanyDetail(false);
        }
    }, [companyPickerUsers.length]);

    const handleCompanySelect = (user) => {
        setSelectedCompany(user);
        applyAccountSwitch(dispatch, user);
        setShowCompanyDetail(true);
    };

    const handlePaymentClick = (user) => {
        if (!user) return;
        applyAccountSwitch(dispatch, user);
        navigate('/documents', {
            state: {
                openBuhBill: true,
                companyId: user.company_id || null,
                accountUserId: user.user_id,
            },
        });
    };

    const handleAddCompany = () => {
        setIsSingleDetailMenuOpen(false);
        setShowAddCompanyModal(true);
    };

    const handleRemoveFromWork = async () => {
        const company =
            selectedCompany ||
            companyPickerUsers.find((user) => Number(user.user_id) === Number(user_id)) ||
            null;
        if (!company?.company_id || !businessAccountId || removingFromWork) return;
        setRemovingFromWork(true);
        try {
            const result =
                company.access_mode === 'delegated'
                    ? await setEmployeeCompanyInWork(businessAccountId, company.company_id, false)
                    : await setBusinessCompanyInWork(businessAccountId, company.company_id, false);
            if (!result.success) {
                alert(result.error || 'Не удалось убрать компанию');
                return;
            }
            setShowCompanyDetail(false);
            setSelectedCompany(null);
            await loadCompanies();
        } catch (e) {
            console.error(e);
            alert('Ошибка обновления статуса');
        } finally {
            setRemovingFromWork(false);
            setIsSingleDetailMenuOpen(false);
        }
    };

    const logoutAccount = () => {
        const accountName = users?.[0]?.company_name || current_user?.name || 'аккаунта';
        const confirmation = window.confirm(`Точно выйти из аккаунта ${accountName}?`);
        if (!confirmation) return;
        localStorage.removeItem('userList');
        localStorage.removeItem('user_id');
        dispatch(setUsers([]));
        dispatch(setIsLogOut(true));
        navigate('/');
    };

    const openDeleteAccount = (user) => {
        setDeleteTargetUser(user || users?.[0] || { user_id });
        setIsModalDeleteAccount(true);
    };

    const deleteCurrentUser = () => {
        const targetId = deleteTargetUser?.user_id || user_id;
        createTask(targetId, 'Удалить аккаунт', 'Удалить аккаунт', {
            timeout: new Date(),
            urgency: 1,
        }, (data) => {
            console.log('createTask', data);
        });
        setIsModalDeleteAccount(false);
        setDeleteTargetUser(null);
    };

    useEffect(() => {
        if (!user_id || user_id === 0) {
            navigate('/');
        }
    }, [user_id, navigate]);

    useEffect(() => {
        if (!companyPickerUsers.length) return undefined;

        const loadForList = !showCompanyDetail;
        const targetUsers = loadForList
            ? companyPickerUsers
            : companyPickerUsers.filter(
                (u) =>
                    Number(u.user_id) === Number(user_id) ||
                    (selectedCompany?.company_id &&
                        Number(u.company_id) === Number(selectedCompany.company_id))
            );

        if (!targetUsers.length) return undefined;

        let cancelled = false;
        const loadPayments = async () => {
            const entries = await Promise.all(
                targetUsers.map(async (user) => {
                    try {
                        const ownerId =
                            user.company_owner_user_id ||
                            user.business_user_id ||
                            businessAccountId ||
                            user.user_id;
                        const packs = await getPacks(
                            ownerId,
                            'buh_bill',
                            user.company_id ? { company_id: user.company_id } : {}
                        );
                        return [user.user_id, buildBuhBillPaymentSummary(packs)];
                    } catch (error) {
                        console.error('Ошибка загрузки счетов:', user.user_id, error);
                        return [user.user_id, buildBuhBillPaymentSummary([])];
                    }
                })
            );
            if (!cancelled) {
                setPaymentByUserId((prev) => ({
                    ...prev,
                    ...Object.fromEntries(entries),
                }));
            }
        };
        loadPayments();
        return () => {
            cancelled = true;
        };
    }, [companyPickerUsers, showCompanyDetail, user_id, businessAccountId, selectedCompany?.company_id]);

    useEffect(() => {
        const fetchData = async () => {
            if (!showCompanyDetail) return;
            const company =
                selectedCompany ||
                companyPickerUsers.find((u) => Number(u.user_id) === Number(user_id));
            if (company && !company.has_legacy_user) {
                setClient({
                    name: company.company_name,
                    inn: company.inn,
                    address: company.address,
                    phone: null,
                    email: null,
                    ceo: null,
                    tax: null,
                    acc_name: null,
                    acc_email: null,
                    acc_phone: null,
                    acc_path: null,
                });
                return;
            }
            if (!user_id) return;
            try {
                const client_data = await getUserData(user_id);
                setClient(client_data.user[0] || {});
            } catch (error) {
                console.error('Ошибка при получении данных пользователя:', error);
            }
        };
        fetchData();
    }, [user_id, showCompanyDetail, selectedCompany, companyPickerUsers]);

    const singleDetailMenuItems = [
        {
            label: 'Добавить компанию',
            icon: addAccountIcon,
            onClick: handleAddCompany,
        },
        {
            label: 'Убрать из работы',
            icon: deleteIcon,
            onClick: handleRemoveFromWork,
        },
        {
            label: 'Выйти',
            icon: outIcon,
            onClick: logoutAccount,
        },
        {
            label: 'Удалить аккаунт',
            icon: deleteIcon,
            danger: true,
            onClick: () => openDeleteAccount(users[0] || { user_id }),
        },
    ];

    const detailCompanyUser = showCompanyDetail
        ? selectedCompany ||
          companyPickerUsers.find((user) => Number(user.user_id) === Number(user_id)) ||
          null
        : null;
    const detailCompanyPayment = detailCompanyUser
        ? paymentByUserId[detailCompanyUser.user_id]
        : null;
    const companyDisplayName =
        detailCompanyUser?.company_name ||
        current_user?.name ||
        current_user?.company_name ||
        client?.name ||
        '';

    useSectionMenuReset('/client', () => {
        if (showCompanyDetail) {
            setShowCompanyDetail(false);
            return true;
        }
        return false;
    });

    return (
        <div className={`client_cntr client_style client-page${!showCompanyDetail ? ' client-page--multi' : ''}`}>
            {!showCompanyDetail ? (
                <div className="page_title_cntr client_page_title_row client_page_title_row--companies">
                    <div className="client_page_title_bar">
                        <div className="client_page_title_center">
                            <div className="manrope-bold color_white page_title client_companies_heading">
                                Мои компании
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="page_title_cntr client_page_title_row client_page_title_row--detail">
                    <div className="client_page_title_bar">
                        <button
                            type="button"
                            className="page-header-nav-back"
                            onClick={() => setShowCompanyDetail(false)}
                            aria-label="К списку компаний"
                        >
                            {arrowLIcon}
                        </button>
                        <div className="manrope-bold color_white section-company-title client_detail_company_name">
                            {companyDisplayName}
                        </div>
                    </div>
                </div>
            )}

            <div className="client_subcntr">
                {!showCompanyDetail ? (
                    companiesLoading ? (
                        <div className="manrope-bold" style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                            Загрузка…
                        </div>
                    ) : companyPickerUsers.length === 0 ? (
                        <div className="manrope-bold" style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                            Компании пока не добавлены. Добавьте компанию в Профиль → Компании.
                        </div>
                    ) : (
                        <CompanyPickerList
                            users={companyPickerUsers}
                            onSelect={handleCompanySelect}
                            onPaymentClick={handlePaymentClick}
                            showPaymentInfo
                            showServiceStatus
                            paymentByUserId={paymentByUserId}
                        />
                    )
                ) : detailCompanyUser && !detailCompanyUser.has_legacy_user ? (
                    <div className="client_card_cntr manrope-bold">
                        {detailCompanyPayment && (
                            <CompanyPaymentBlock
                                payment={detailCompanyPayment}
                                user={detailCompanyUser}
                                onPaymentClick={handlePaymentClick}
                            />
                        )}
                        <div className="client_row">
                            <div className="client_row-label color_grey">Название</div>
                            <div className="client_row-value">{detailCompanyUser.company_name}</div>
                        </div>
                        <div className="client_row">
                            <div className="client_row-label color_grey">ИНН</div>
                            <div className="client_row-value">{detailCompanyUser.inn || client?.inn || '—'}</div>
                        </div>
                        <div className="client_row">
                            <div className="client_row-label color_grey">Адрес</div>
                            <div className="client_row-value client_row-value--right">
                                {detailCompanyUser.address || client?.address || '—'}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="client_card_cntr manrope-bold">
                            {detailCompanyUser && detailCompanyPayment && (
                                <CompanyPaymentBlock
                                    payment={detailCompanyPayment}
                                    user={detailCompanyUser}
                                    onPaymentClick={handlePaymentClick}
                                />
                            )}
                            <div className="client_row">
                                <div className="client_row-label color_grey">Email</div>
                                <div className="client_row-value">{client?.email}</div>
                            </div>
                            <div className="client_row">
                                <div className="client_row-label color_grey">Телефон</div>
                                <div className="client_row-value">{client?.phone}</div>
                            </div>
                            <div className="client_row">
                                <div className="client_row-label color_grey">ИНН</div>
                                <div className="client_row-value">{client?.inn || detailCompanyUser?.inn}</div>
                            </div>
                            <div className="client_row">
                                <div className="client_row-label color_grey">Руководитель</div>
                                <div className="client_row-value client_row-value--right">{client?.ceo}</div>
                            </div>
                            <div className="client_row">
                                <div className="client_row-label color_grey">Налогооблажение</div>
                                <div className="client_row-value">{client?.tax}</div>
                            </div>
                        </div>
                        <div className="client_card2_cntr manrope-bold">
                            <div className="client_row">
                                <div style={{ fontSize: 14 }} className="color_white">Бухгалтер</div>
                                <div style={{ fontSize: 16 }} className="color_white">{client?.acc_name}</div>
                            </div>
                            <div className="client_row">
                                <div style={{ fontSize: 14 }} className="color_white">Email</div>
                                <div className="color_white client_label">{client?.acc_email}</div>
                            </div>
                            <div className="client_row">
                                <div style={{ fontSize: 14 }} className="color_white">Телефон</div>
                                <div style={{ fontSize: 16 }} className="color_white">{client?.acc_phone}</div>
                            </div>
                            <div className="client_row">
                                <img className="client_acc_img" alt="" src={`${url}/${client?.acc_path}`} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {companyMenuUser && (
                <ClientMenuModal
                    onClose={() => setCompanyMenuUser(null)}
                    items={[
                        {
                            label: 'Выйти',
                            icon: outIcon,
                            onClick: logoutAccount,
                        },
                    ]}
                />
            )}

            {isSingleDetailMenuOpen && (
                <ClientMenuModal
                    onClose={() => setIsSingleDetailMenuOpen(false)}
                    items={singleDetailMenuItems}
                />
            )}

            {isModalDeleteAccount && (
                <DeleteModal
                    deleteCurrentUser={deleteCurrentUser}
                    onClose={() => {
                        setIsModalDeleteAccount(false);
                        setDeleteTargetUser(null);
                    }}
                />
            )}

            {showAddCompanyModal && businessAccountId && (
                <AddWorkspaceCompanyModal
                    businessUserId={businessAccountId}
                    onClose={() => setShowAddCompanyModal(false)}
                    onAdded={async () => {
                        await loadCompanies();
                        setShowCompanyDetail(false);
                    }}
                />
            )}
        </div>
    );
};

export default Client;
